import { supabase } from "@/integrations/supabase/client";
import type { UserConfigurationState, Role, RateMultiplier, ShortTurnConfig, BillingStructureConfig } from "@/types";
import type { ExtendedTier } from '@/types/ExtendedTier';
import type { Tables, Json } from "@/integrations/supabase/types";
import Logger from "@/utils/logger";
import type { MealBreakConfig } from '@/types/MealBreakConfig';
import type { OvernightConfig } from '@/types/OvernightConfig';
import type { ExpenseConfig } from '@/types/ExpenseConfig';
import type { ExpenseCategory } from '@/types/ExpenseCategory';
import type { GearConfig, GearItem, GearKit, CartageConfig } from '@/types/GearConfig';
import { DEFAULT_GEAR_CONFIG } from '@/types/GearConfig';

// Row returned by the `get_user_configuration` RPC/View
type UserConfigurationRow = Tables<"user_configuration">;

export type UserConfigRecord = {
  profile: Tables<"user_profile_config">;
  billing: Tables<"user_billing_config">;
  invoice: Tables<"user_invoice_config">;
  roles: Tables<"user_roles_config">[];
  multipliers: Tables<"user_rate_multiplier_config">[];
  travel: Tables<"user_travel_config"> | null;
  short_turn: Tables<"user_short_turn_config"> | null;
};

const parseDecimal = (val: unknown): number | null => {
  if (val === null || val === undefined) return null;
  const num = parseFloat(val as string);
  return isNaN(num) ? null : num;
};

const parseExtendedTiers = (val: unknown): ExtendedTier[] => {
  if (!val || !Array.isArray(val)) return [];
  return val.map((t: Record<string, unknown>) => ({
    name: (t.name as string) || '',
    rate: Number(t.rate ?? 0),
    sortOrder: Number(t.sortOrder ?? t.sort_order ?? 0),
  }));
};

const transformDbToState = (dbData: UserConfigurationRow): UserConfigurationState => {
  const rolesFromDb = (dbData.roles || []) as unknown as Role[];
  const multipliersFromDb = ((dbData.rate_multipliers || []) as unknown as Record<string, unknown>[]).map(
    (m): RateMultiplier => ({
      id: m.id as string | undefined,
      code: m.code as string,
      name: m.name as string,
      defaultMultiplier: m.defaultMultiplier != null ? Number(m.defaultMultiplier) : m.default_multiplier != null ? Number(m.default_multiplier) : null,
      defaultPerShiftThresholdHours: m.defaultPerShiftThresholdHours != null ? Number(m.defaultPerShiftThresholdHours) : m.default_per_shift_threshold_hours != null ? Number(m.default_per_shift_threshold_hours) : null,
      defaultPerWeekThresholdHours: m.defaultPerWeekThresholdHours != null ? Number(m.defaultPerWeekThresholdHours) : m.default_per_week_threshold_hours != null ? Number(m.default_per_week_threshold_hours) : null,
      perShiftBillingStructures: (m.perShiftBillingStructures ?? m.per_shift_billing_structures) as string[] | null,
      perWeekBillingStructures: (m.perWeekBillingStructures ?? m.per_week_billing_structures) as string[] | null,
    })
  );

  const shortTurnConfig: ShortTurnConfig | null = dbData.default_uses_short_turn
    ? {
        requiredBreakHours: parseDecimal(dbData.default_required_break_hours),
        surchargeType: (dbData.default_short_turn_surcharge_type as ShortTurnConfig['surchargeType']) || 'carry_over',
        shortTurnFixedSurchargeAmount: parseDecimal(dbData.default_short_turn_fixed_surcharge_amount),
        surchargeAppliesToShortedHoursOnly: dbData.surcharge_applies_to_shorted_hours_only ?? false,
        premiumBasis: (dbData.short_turn_premium_basis as 'base_rate' | 'ot_inclusive') ?? 'base_rate',
        premiumType: (dbData.short_turn_premium_type as ShortTurnConfig['premiumType']) || null,
        premiumValue: parseDecimal(dbData.short_turn_premium_value),
        applyOnHalfDays: dbData.default_apply_on_half_days ?? true,
        applicableRoleIds: ((dbData as unknown as Record<string, unknown>).short_turn_applicable_role_ids as string[] | null) ?? null,
        extendedOvertimeEnabled: ((dbData as unknown as Record<string, unknown>).short_turn_extended_overtime_enabled as boolean | undefined) ?? false,
        extendedOvertimeTiers: parseExtendedTiers((dbData as unknown as Record<string, unknown>).short_turn_extended_overtime_tiers),
        // Unified rate-offset columns (Phase 9)
        floorSource: ((dbData.short_turn_floor_source as ShortTurnConfig['floorSource']) ?? 'none'),
        overtimeOffset: parseDecimal(dbData.short_turn_overtime_offset) ?? 1,
        floorTierCode: dbData.short_turn_floor_tier_code ?? null,
        floorTierCustomValue: parseDecimal(dbData.short_turn_floor_tier_custom_value),
      }
    : null;

  const fixedFeeScheduleFromDb = (dbData.fixed_fee_schedule as MealBreakConfig['fixedFeeSchedule']) ?? null;

  const mealConfig: MealBreakConfig = {
    applyMealSurcharge: dbData.apply_meal_surcharge ?? false,
    mealDeadlineHours: parseDecimal(dbData.meal_deadline_hours) ?? 5,
    recurringMealDeadline: dbData.recurring_meal_deadline ?? false,
    mealBreakLengthMinutes: parseDecimal(dbData.meal_break_length_minutes) ?? 30,
    breakBillingPolicy: (dbData.break_billing_policy as MealBreakConfig['breakBillingPolicy']) ?? {
      thresholdMinutes: null,
      upToThreshold: 'unpaid',
      overThreshold: 'unpaid',
    },
    mealSurchargeType: (dbData.meal_surcharge_type as MealBreakConfig['mealSurchargeType']) || 'offset_overtime',
    useAdditionalOffset: dbData.use_additional_offset ?? true,
    creditIntervalMinutes: parseDecimal(dbData.credit_interval_minutes) ?? 60,
    stepUpAfterMinutes: parseDecimal(dbData.step_up_after_minutes) ?? 60,
    timeCreditMinutes: parseDecimal(dbData.time_credit_minutes),
    fixedFeeSchedule: fixedFeeScheduleFromDb,
    fixedFeeAmount: fixedFeeScheduleFromDb && fixedFeeScheduleFromDb.length > 0 ? (fixedFeeScheduleFromDb[0].fee_amount ?? null) : null,
    gracePeriodMinutes: parseDecimal(dbData.grace_period_minutes),
    premiumType: (dbData.meal_premium_type as MealBreakConfig['premiumType']) || null,
    premiumValue: parseDecimal(dbData.meal_premium_value),
    premiumBasis: (dbData.meal_premium_basis as string) ?? 'STRAIGHT_TIME',
    premiumStacking: (dbData.meal_premium_stacking as 'stacked' | 'flat') ?? 'flat',
    applyOnHalfDays: ((dbData as unknown as Record<string, unknown>).meal_apply_on_half_days as boolean | undefined) ?? true,
    applicableRoleIds: ((dbData as unknown as Record<string, unknown>).meal_applicable_role_ids as string[] | null) ?? null,
    extendedOvertimeEnabled: ((dbData as unknown as Record<string, unknown>).meal_extended_overtime_enabled as boolean | undefined) ?? false,
    extendedOvertimeTiers: parseExtendedTiers((dbData as unknown as Record<string, unknown>).meal_extended_overtime_tiers),
    // Unified rate-offset columns (Phase 9). View columns: meal_floor_source,
    // meal_overtime_offset, meal_floor_tier_code, meal_floor_tier_custom_value.
    floorSource: (((dbData as unknown as Record<string, unknown>).meal_floor_source as MealBreakConfig['floorSource']) ?? 'none'),
    overtimeOffset: parseDecimal((dbData as unknown as Record<string, unknown>).meal_overtime_offset) ?? 0,
    floorTierCode: ((dbData as unknown as Record<string, unknown>).meal_floor_tier_code as string | null) ?? null,
    floorTierCustomValue: parseDecimal((dbData as unknown as Record<string, unknown>).meal_floor_tier_custom_value),
    mealTriggerMode: ((dbData as unknown as Record<string, unknown>).meal_trigger_mode as MealBreakConfig['mealTriggerMode']) ?? 'deadline',
    requiredBreaks: parseDecimal((dbData as unknown as Record<string, unknown>).required_breaks),
    thresholdHours: parseDecimal((dbData as unknown as Record<string, unknown>).threshold_hours),
    additionalBreakIntervalHours: parseDecimal((dbData as unknown as Record<string, unknown>).additional_break_interval_hours),
    requiredBreaksHalfDay: parseDecimal((dbData as unknown as Record<string, unknown>).required_breaks_half_day),
  };

  const overnightConfig: OvernightConfig = {
    applyOvernightPremium: dbData.apply_overnight_premium ?? false,
    startTime: dbData.overnight_start_time ?? null,
    endTime: dbData.overnight_end_time ?? null,
    overnightMethod: (dbData.overnight_premium_type as OvernightConfig['overnightMethod']) || null,

    // Activation Conditions
    thresholdEnabled: !!((dbData as unknown as Record<string, unknown>).overnight_percentage_threshold_enabled || (dbData as unknown as Record<string, unknown>).overnight_hours_threshold_enabled),
    thresholdType: (dbData as unknown as Record<string, unknown>).overnight_hours_threshold_enabled ? 'hours' : ((dbData as unknown as Record<string, unknown>).overnight_percentage_threshold_enabled ? 'percentage' : null),
    percentageThreshold: parseDecimal(dbData.overnight_percentage_threshold),
    hoursThreshold: parseDecimal(dbData.overnight_hours_threshold),

    premiumType: (dbData.overnight_hourly_premium_type as OvernightConfig['premiumType']) || null,
    premiumValue: parseDecimal(dbData.overnight_hourly_premium_value),
    flatRateAmount: parseDecimal(dbData.overnight_flat_rate_amount),
    timeCreditMinutes: parseDecimal(dbData.overnight_time_credit_minutes),
    premiumBasis: (dbData.overnight_hourly_premium_basis as 'base_rate' | 'ot_inclusive') ?? 'base_rate',
    applyOnHalfDays: ((dbData as unknown as Record<string, unknown>).overnight_apply_on_half_days as boolean | undefined) ?? true,
    applicableRoleIds: ((dbData as unknown as Record<string, unknown>).overnight_applicable_role_ids as string[] | null) ?? null,
    extendedOvertimeEnabled: ((dbData as unknown as Record<string, unknown>).overnight_extended_overtime_enabled as boolean | undefined) ?? false,
    extendedOvertimeTiers: parseExtendedTiers((dbData as unknown as Record<string, unknown>).overnight_extended_overtime_tiers),
    // Unified rate-offset columns (Phase 9). View columns: overnight_floor_source,
    // overnight_overtime_offset, overnight_floor_tier_code, overnight_floor_tier_custom_value.
    floorSource: (((dbData as unknown as Record<string, unknown>).overnight_floor_source as OvernightConfig['floorSource']) ?? 'none'),
    overtimeOffset: parseDecimal((dbData as unknown as Record<string, unknown>).overnight_overtime_offset) ?? 0,
    floorTierCode: ((dbData as unknown as Record<string, unknown>).overnight_floor_tier_code as string | null) ?? null,
    floorTierCustomValue: parseDecimal((dbData as unknown as Record<string, unknown>).overnight_floor_tier_custom_value),
  };

  const billingStructureConfig: BillingStructureConfig = {
    minimumBillableHours: parseDecimal(dbData.minimum_billable_hours),
    halfDayHours: parseDecimal(dbData.half_day_hours),
    weekStart: dbData.week_start,
    perWeekDayRateCalculationBasis: (dbData.per_week_day_rate_calculation_basis as BillingStructureConfig['perWeekDayRateCalculationBasis']) || 'actual',
  };

  const gearConfig: GearConfig = (dbData as unknown as Record<string, unknown>).gear_items
    ? {
        gearItems: ((dbData as unknown as Record<string, unknown>).gear_items as GearItem[]) ?? [],
        gearKits: ((dbData as unknown as Record<string, unknown>).gear_kits as GearKit[]) ?? [],
        cartage: ((dbData as unknown as Record<string, unknown>).cartage as CartageConfig) ?? { method: 'none' },
        weekLengthDays: ((dbData as unknown as Record<string, unknown>).gear_week_length_days as number | undefined) ?? 5,
      }
    : DEFAULT_GEAR_CONFIG;

  const expenseConfig: ExpenseConfig = {
    expenseCategories: (dbData.expense_categories as unknown as ExpenseCategory[]) ?? [],
    expensePolicies: dbData.expense_policies as ExpenseConfig['expensePolicies'] ?? {},
    expenseMileageRate: parseDecimal(dbData.expense_mileage_rate),
    expenseMileageUnit: (dbData.expense_mileage_unit as ExpenseConfig['expenseMileageUnit']) || 'miles',
    defaultExpenseMarkupType: ((dbData as unknown as Record<string, unknown>).default_expense_markup_type as ExpenseConfig['defaultExpenseMarkupType']) || 'none',
    defaultExpenseMarkupPercentage: parseDecimal((dbData as unknown as Record<string, unknown>).default_expense_markup_percentage),
    defaultExpenseMarkupFixedAmount: parseDecimal((dbData as unknown as Record<string, unknown>).default_expense_markup_fixed_amount),
    expenseReceiptRequiredAboveAmount: parseDecimal((dbData as unknown as Record<string, unknown>).expense_receipt_required_above_amount),
  };

  return {
    // Profile
    firstName: dbData.first_name || '',
    lastName: dbData.last_name || '',
    email: dbData.email || '',
    phone: dbData.phone || '',

    // Billing
    billingStructure: null, // UI-only state, not in DB view
    billingStructureConfig,
    allowPremiumShifts: dbData.allow_premium_shifts || false,
    premiumShiftOffsetMode: (dbData.premium_shift_offset_mode as UserConfigurationState['premiumShiftOffsetMode']),
    premiumShiftFixedOffset: parseDecimal(dbData.premium_shift_fixed_offset),
    premiumShiftMultiplierCode: dbData.premium_shift_multiplier_code,
    usesShortTurn: dbData.default_uses_short_turn || false,
    shortTurnConfig,
    mealConfig,
    overnightConfig,
    overtimeConcurrencyStrategy: (dbData.overtime_concurrency_strategy as UserConfigurationState['overtimeConcurrencyStrategy']) || 'highest_prevailing',
    addFixedSurcharges: dbData.add_fixed_surcharges ?? false,

    // Invoice
    paymentTermsDays: parseDecimal(dbData.payment_terms_days) ?? 30,
    lateFeePercentage: parseDecimal(dbData.late_fee_percentage),
    useLateFee: dbData.use_late_fee || false,
    lateFeeType: (dbData.late_fee_type as UserConfigurationState['lateFeeType']) || 'percentage',
    lateFeeFixedAmount: parseDecimal(dbData.late_fee_fixed_amount),
    recurringLateFee: (dbData.recurring_late_fee as UserConfigurationState['recurringLateFee']) || 'once',
    recurringLateFeeInterval: parseDecimal(dbData.recurring_late_fee_interval),
    isRecurringLateFee: dbData.recurring_late_fee !== 'once', // Derived state
    useEarlyPaymentDiscount: dbData.use_early_payment_discount || false,
    earlyPaymentType: (dbData.early_payment_type as UserConfigurationState['earlyPaymentType']) || 'percentage',
    earlyPaymentDiscountPercentage: parseDecimal(dbData.early_payment_discount_percentage),
    earlyPaymentFixedAmount: parseDecimal(dbData.early_payment_fixed_amount),
    earlyPaymentDays: parseDecimal(dbData.early_payment_days),
    autoSendReminders: dbData.auto_send_reminders === null ? true : dbData.auto_send_reminders,
    reminderDays: dbData.reminder_days || [],
    externalPayments: dbData.external_payments || false,
    employmentType: (dbData.employment_type as UserConfigurationState['employmentType']) || 'W2',
    xeroId: dbData.xero_id,

    useCancellationPolicy: dbData.use_cancellation_policy || false,
    cancellationPolicy: (dbData.cancellation_policy as UserConfigurationState['cancellationPolicy']) || [],
    rushPremiumPolicy: (dbData.rush_premium_policy as UserConfigurationState['rushPremiumPolicy']) || [],

    // Roles and Multipliers
    roles: rolesFromDb,
    rateMultipliers: multipliersFromDb,
    
    // Holiday Premium
    applyHolidayPremium: dbData.apply_holiday_premium || false,
    holidayCatalogIds: (dbData as unknown as Record<string, unknown>).catalog_holiday_ids as string[] ?? [],
    holidayCustomIds: (dbData as unknown as Record<string, unknown>).custom_holiday_ids as string[] ?? [],
    countryCodes: (dbData as unknown as Record<string, unknown>).country_codes as string[] ?? ['US'],
    holidayPremiumType: (dbData.holiday_premium_type as UserConfigurationState['holidayPremiumType']) || 'offset_overtime',
    // Unified rate-offset columns (Phase 9)
    holidayFloorSource: ((dbData.holiday_floor_source as UserConfigurationState['holidayFloorSource']) ?? 'none'),
    holidayOvertimeOffset: parseDecimal(dbData.holiday_overtime_offset) ?? 1,
    holidayFloorTierCode: dbData.holiday_floor_tier_code ?? null,
    holidayFloorTierCustomValue: parseDecimal(dbData.holiday_floor_tier_custom_value),
    holidayFixedPremiumAmount: parseDecimal(dbData.holiday_fixed_premium_amount),
    surchargeConcurrencyStrategy: (dbData.surcharge_concurrency_strategy as UserConfigurationState['surchargeConcurrencyStrategy']) || 'highest_prevailing',
    holidayHourlyPremiumType: (dbData.holiday_hourly_premium_type as UserConfigurationState['holidayHourlyPremiumType']) || null,
    holidayHourlyPremiumValue: parseDecimal(dbData.holiday_hourly_premium_value),
    holidayPremiumBasis: (dbData.holiday_premium_basis as 'base_rate' | 'ot_inclusive') ?? 'base_rate',
    holidayApplyOnHalfDays: ((dbData as unknown as Record<string, unknown>).holiday_apply_on_half_days as boolean | undefined) ?? true,
    holidayApplicableRoleIds: ((dbData as unknown as Record<string, unknown>).holiday_applicable_role_ids as string[] | null) ?? null,
    holidayExtendedOvertimeEnabled: ((dbData as unknown as Record<string, unknown>).holiday_extended_overtime_enabled as boolean | undefined) ?? false,
    holidayExtendedOvertimeTiers: parseExtendedTiers((dbData as unknown as Record<string, unknown>).holiday_extended_overtime_tiers),
    
    // Travel
    needsTravel: !!(dbData.apply_per_diem || dbData.per_diem_base_rate || dbData.travel_day_rate_multiplier || dbData.uses_gsa_rate),
    usesGsaRate: dbData.uses_gsa_rate || false,
    applyPerDiem: dbData.apply_per_diem || false,
    perDiemBaseRate: parseDecimal(dbData.per_diem_base_rate),
    travelDayRateMultiplier: parseDecimal(dbData.travel_day_rate_multiplier),
    multiplierAppliesToOverages: dbData.multiplier_applies_to_overages,
    travelDistanceThreshold: dbData.travel_distance_threshold ? Number(dbData.travel_distance_threshold) : null,

    // Expenses
    expenseConfig,

    // Gear / Kit Fees
    gearConfig,
  };
};

export async function getUserConfig(
  userId: string
): Promise<UserConfigurationState | null> {
  const { data, error } = await supabase
    .rpc(
      "get_user_configuration",
      { p_user_id: userId }
    )
    .maybeSingle();
    
  if (error) {
    Logger.error('Failed to fetch user configuration', error, {
      component: 'userConfigService',
      userId
    });
    throw error;
  }
  
  if (!data) {
    return null;
  }

  return transformDbToState(data as UserConfigurationRow);
}

/** Persist *all* onboarding / profile data in a single request.*/

export async function saveUserConfig(userId: string, state: UserConfigurationState) {
  // Build the payload the RPC expects. The RPC reads camelCase keys for the
  // catalog/custom/country fields (verified: jsonb_exists(payload, 'catalogHolidayIds')).
  //
  // Unified rate-offset payload (Phase 9). The RPC reads the nested camelCase keys
  // from shortTurnConfig directly — we pass the unified field names through as-is.
  const { shortTurnConfig, ...restState } = state;
  const shortTurnConfigPayload = shortTurnConfig
    ? ({ ...(shortTurnConfig as unknown as Record<string, unknown>) })
    : null;
  const payload = {
    ...(restState as unknown as Record<string, unknown>),
    shortTurnConfig: shortTurnConfigPayload,
    catalogHolidayIds: state.holidayCatalogIds,
    customHolidayIds: state.holidayCustomIds,
    countryCodes: state.countryCodes,
  };

  // Give Supabase 15 s to respond, then reject so the UI can recover.
  const rpcPromise = supabase.rpc("upsert_user_configuration", {
    p_user_id: userId,
    payload: payload as unknown as Json
  });

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("Request timed out")), 15_000)
  );

  const { error } = await Promise.race([rpcPromise, timeoutPromise]);

  if (error) {
    Logger.error('Failed to save user configuration', error, {
      component: 'userConfigService',
      userId
    });
    throw error;
  }

}

/**
 * Checks if a specific user role is in use by any of the user's clients.
 * @param roleId The UUID of the user_roles_config entry.
 * @returns A promise that resolves to true if the role is in use, false otherwise.
 */
export async function isRoleInUse(roleId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_role_in_use', { p_role_id: roleId });

  if (error) {
    Logger.error('Failed to check if role is in use', error, {
      component: 'userConfigService',
      roleId,
    });
    throw new Error('Could not verify role usage. Deletion prevented as a precaution.');
  }

  return data;
}

/**
 * Gets detailed usage information for a specific rate multiplier.
 * @param multiplierCode The code of the user_rate_multiplier_config entry.
 * @returns A promise that resolves to usage details including client names, user default settings, and booking history.
 */
interface MultiplierUsageDetails {
  has_booking_history: boolean;
  client_names_with_history: string[];
  client_names_config_only: string[];
  default_usages: string[];
}

export async function getMultiplierUsageDetails(multiplierCode: string): Promise<MultiplierUsageDetails> {
  const { data, error } = await supabase.rpc('get_multiplier_usage_details', { p_multiplier_code: multiplierCode });

  if (error) {
    Logger.error('Failed to get multiplier usage details', error, {
      component: 'userConfigService',
      multiplierCode,
    });
    throw new Error('Could not get multiplier usage details. Please try again.');
  }

  const usageData = data as unknown as MultiplierUsageDetails;

  return usageData;
}

/**
 * Gets detailed usage information for a specific user role.
 * @param roleId The UUID of the user_roles_config entry.
 * @returns A promise that resolves to usage details including client names and booking history.
 */
interface RoleUsageDetails {
  has_booking_history: boolean;
  client_names: string[];
}

export async function getRoleUsageDetails(roleId: string): Promise<RoleUsageDetails> {
  const { data, error } = await supabase.rpc('get_role_usage_details', { p_role_id: roleId });

  if (error) {
    Logger.error('Failed to get role usage details', error, {
      component: 'userConfigService',
      roleId,
    });
    throw new Error('Could not get role usage details. Please try again.');
  }

  const usageData = data as unknown as RoleUsageDetails;

  return usageData;
}
